import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ValueFormatAttributesUtils } from "abweb/dto/ds/ValueFormatAttributesUtils";
import { DataFieldTypeEnum } from "abweb/dto/generated/ds/DataFieldTypeEnum";
import { DataSourceSaveAction } from "abweb/dto/generated/ds/DataSourceSaveAction";
import { DataSourceState } from "abweb/dto/generated/ds/DataSourceState";
import { DataSourceValueBase } from "abweb/dto/generated/ds/DataSourceValueBase";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { StringUtils } from "abweb/utils/StringUtils";
export class DataSourceValue extends DataSourceValueBase {
  /**
   * For MH-29905, we are adding grandfathering like metadata validation rules for entity api world.
   * If a saved value has not been changed locally, then if the saved value was invalid, it does not prevent saving
   * True indicates the error does block saving, false does not block saving.
   * This is initially only used when redirecting validation issues to the ValidationProvider system on configured details pages
   *
   * NOTE: This describes the validationError if it exists. If there is no validationError, ignore this value.
   *
   * Does not need to be kendo observable, should only be looked at when validationError has changed.
   */
  get validationErrorBlocksSave() {
    return this._validationErrorBlocksSave;
  }
  constructor() {
    super();
    /**
     * Indicates if this value has a locally changed value.
     * Please do not set this directly. It should be set only by the internal workings of this class.
     * Its setter is public because of Kendo voodoo.
     * @type {boolean}
     */
    _defineProperty(this, "hasLocalChange", false);
    /**
     * Indicates the local state of the value when changes are made via the UI.
     * Please do not set this directly. It should be set only by the internal workings of this class.
     * Its setter is public because of Kendo voodoo.
     * @type {DataSourceState}
     */
    _defineProperty(this, "localState", DataSourceState.NORMAL);
    /**
     * Validation error for this value.
     * Typically, you do not have to set this value. It will get updated via the DataSourceValidation object associated
     * with the _field field. If you do set it directly, use the Kendo set("validationError", value) API so that proper
     * notifications are fired.
     * @type {string}
     */
    _defineProperty(this, "validationError", "");
    /**
     * See get validationErrorBlockSave()
     */
    _defineProperty(this, "_validationErrorBlocksSave", true);
    this.bind("change", e => this.changeHandler(e));
  }
  initFromJson(json, deserializeContext) {
    this.initDataSourceValue(json.attributeValues != null ? json.attributeValues : null, json.clientTag, json.dataSourceSaveAction, json.editableOverwrite, json.id, json.state);
  }
  get hasRef() {
    // Overridden in DataSourceBaseValue
    return false;
  }

  // Can not have 'null' refID implementation as generated DataSourceBaseValue has refID (without 'overide' keyword)
  // Instead, define as abstract to force all non-abstract descendants to implement refID

  get editable() {
    if (this.editableOverwrite != null) {
      return this.editableOverwrite;
    }
    if (this._containerRecord != null) {
      if (this._containerRecord.isVector) {
        // Use the editable state of the containing Vector and look no further
        return this._containerRecord.editable;
      }
      if (!this._containerRecord.editable) {
        return false;
      }
    }
    return this._field && this._field.editable ? true : false;
  }
  get fieldType() {
    return this.getFieldType();
  }

  /**
   * Protected method to work around TS2340 compiler issue which prevents us from calling super.fieldType when
   * we override the fieldType getter.
   * @returns {DataFieldTypeEnum}
   */
  getFieldType() {
    return this._field ? this._field.fieldType : null;
  }

  /**
   * Gets the current value part of this DataSourceValue.
   * Initially, the currentValue is the same as value, but if the client
   * makes local changes, the currentValue represents those local changes.
   * This getter MUST be overridden by subclasses to return the value field.
   */

  /**
   * Gets the initial value part of this DataSourceValue.
   * This getter MUST be overridden by subclasses to return the value field.
   */

  /**
   * Gets the current baseline part of this DataSourceValue.
   * This getter MUST be overridden by subclasses to return the value field.
   */

  toString() {
    return this.getDisplayValue();
  }

  /**
   * Gets the currentValue in a canonical form for editing.
   * Typically this is simply the currentValue, but may be overridden by subclasses.
   * @returns {any}
   */
  getCanonicalEditValue() {
    return this.getCurrentValue();
  }
  getFormattedValue(value) {
    return value == null ? "" : value.toLocaleString();
  }

  /**
   * Returns a custom formatted value based on format attributes passed in.
   * @param value
   * @param formatOptions
   */
  getFormattedValue2(value, formatOptions) {
    let coercedType = this.fieldType;
    if (formatOptions.valueFormatType != null) {
      const tuple = this.coerceValueIfNecessary(value, formatOptions);
      value = tuple.value;
      coercedType = tuple.coercedType;
    }
    if (coercedType === DataFieldTypeEnum.DECIMAL) {
      return ValueFormatAttributesUtils.formatNumberAsString(value, formatOptions, true);
    } else if (coercedType === DataFieldTypeEnum.DATE) {
      return ValueFormatAttributesUtils.formatDateAsString(value, formatOptions, true);
    } else if (coercedType === DataFieldTypeEnum.DATETIME) {
      return ValueFormatAttributesUtils.formatDatetimeAsString(value, formatOptions, true);
    } else {
      return this.getFormattedValue(value);
    }
  }
  coerceValueIfNecessary(value, formatOptions) {
    const ret = {
      value: value,
      coercedType: this.fieldType
    };
    if (formatOptions.valueFormatType == null) {
      // Use as is
      return ret;
    }
    switch (formatOptions.valueFormatType) {
      case "Number":
      case "Currency":
      case "Percentage":
        // Note that if we can't coerce the value, we'll get NaN here
        ret.coercedType = DataFieldTypeEnum.DECIMAL;
        if (value == null) {
          ret.value = null;
        } else {
          ret.value = Number(value);
        }
        break;
      case "Date":
      case "DateTime":
        ret.coercedType = formatOptions.valueFormatType === "DateTime" ? DataFieldTypeEnum.DATETIME : DataFieldTypeEnum.DATE;
        if (value == null) {
          ret.value = null;
        } else if (ClassUtils.isInstanceOf(value, Date)) {
          // No need to coerce since we already have a Date object
          ret.value = value;
        } else {
          ret.value = DateTimeUtils.kendoParseDate(value.toString());
        }
        break;
      default:
        break;
    }
    return ret;
  }

  /**
   * Test for equality of 2 values.
   * Note that subclasses may override this to provide a type-safe value comparison method.
   * @returns {boolean} true if values are equal.
   */
  areEqual(value1, value2) {
    return value1 === value2;
  }
  changeHandler(event) {
    if (event.field === "currentValue") {
      // We are changing currentValue
      this.onCurrentValueChanged();
    } else if (event.field === "localState") {
      this.onLocalStateChanged();
    } else if (event.field === "validationError") {
      this.onValidationErrorChanged();
    }
  }
  onCurrentValueChanged() {
    //console.log("DataSourceValue.onCurrentValueChanged ");

    this.updateLocalState();
    this.updateValidationError();
    if (this._dataSource) {
      // DataSourceValue.LOG.debug("onCurrentValueChanged calling _dataSource.onContainedValueChanged()");
      this._dataSource.onContainedValueChanged(this);
    }
  }
  onLocalStateChanged() {
    this.set("hasLocalChange", this.localState !== DataSourceState.NORMAL);
    if (this._containerRecord) {
      this._containerRecord.onContainerValueLocalStateChanged(this);
    }
  }
  onValidationErrorChanged() {
    if (this._containerRecord) {
      this._containerRecord.onContainerValueValidationErrorChanged(this);
    }
  }

  /**
   * Notification for a _containerRecord from a contained value that its localState value has changed.
   * We update our local value of hasLocalChange based on this change.
   * @param dsv
   * @returns {boolean} Value of hasLocalChange
   */
  onContainerValueLocalStateChanged(dsv) {
    if (this.isAddedLocal) {
      // Ignore this since we are added and this state trumps CHANGE
      return true;
    }
    if (this.isDeletedLocal || this.isDeletedLocal) {
      DataSourceValue.LOG.warn("onContainerValueLocalStateChanged state = DELETE. Should not be changing contained values.");
      return true;
    }
    if (dsv.hasLocalChange) {
      this.set("localState", DataSourceState.CHANGE);
      return true;
    }

    // Value has transitioned from true to false so we have to check all values
    this.updateLocalStateValue();
    return this.hasLocalChange;
  }
  onContainerValueValidationErrorChanged(dsv) {
    // base class does nothing, but will be overridden in DataSourceRecord
  }

  /**
   * Notification from a _containerRecord to its contained values that the localState has changed.
   * Example of this is when a DataSourceRecord is being deleted or added in the UI.
   * Since the DataSourceValue.isDeleteLocal and isAddedLocal are based on _containerRecord.localState, we will
   * fire a change notification for anyone who cares.
   */
  onContainerRecordLocalStateChanged() {
    this.trigger("change", {
      field: "_containerRecord.localState"
    });
  }

  /**
   * Called for a _containerRecord when one of its contained values has a localState change.
   * This method should update the localState value based on the localStates of its contained values.
   */
  updateLocalStateValue() {
    // Subclasses may override this
  }
  updateLocalState() {
    // Note that do not use the helper isAddedLocal || isDeletedLocal because we only want to consider MY localState
    // and not check _containerRecord.localState
    if (this.localState === DataSourceState.ADD || this.localState === DataSourceState.DELETE) {
      return;
    }
    const localChange = !this.areEqual(this.getCurrentValue(), this.getValue());
    this.set("localState", localChange ? DataSourceState.CHANGE : DataSourceState.NORMAL);
  }
  revertLocalChange() {
    if (!this.hasLocalChange) {
      return false;
    }

    // Get the previous value
    this.set("currentValue", this.getValue());
    return true;
  }

  /**
   * Called every time the currentValue changes, this method updates the validationError based on
   * rules defined in _field.validation.
   * You can also call this method to force the validationError to be updated.
   * This may be needed since the server will not set this value.
   */
  updateValidationError() {
    let error = this.validateValue(this.getCurrentValue());

    /*
      For MH-29905
      If editing an existing record (not isAdded), and if the value hasn't been changed (not this.hasLocalChange),
      then we don't block saving.
      Condition transformed from NOT (NOT A OR NOT B) to A OR B
      (If record is new, or if the value has been changed, we block saving)
      Also added a condition to set this false if there is no error, in case someone is confused about usage.
       This relies on hasLocalChange being updated before getting here.
      In onCurrentValueChanged(), updateLocalState() is called before updateValidationError(), so that is true.
     */
    this._validationErrorBlocksSave = error != null && (this.isAdded || this.hasLocalChange);
    if (error == null) {
      // No error
      if (!this.validationError) {
        // No change
        return;
      }

      // This needs to be non-null
      error = "";
    }
    if (error !== this.validationError) {
      // We have a change
      this.set("validationError", error);
    }
  }

  /**
   * Validates the indicated value against the set of validation rules.
   * Note that the base class only validates requiredness. You should override this in derived classes
   * to provide strongly-typed validation.
   * @param value
   */
  validateValue(value) {
    if (!this._field) {
      // No field
      return null;
    }
    if (this.isRequired() && this.isEmpty()) {
      // Requiredness error
      return AbWebResources.required();
    }
    return null;
  }

  /**
   * Indicates if this value is valid (has no validation errors) or not.
   * @returns {boolean} true if valid, false if not (validationError will be set)
   */
  isValid() {
    return this.get("validationError") ? false : true;
  }

  /**
   * Determines if the currentValue is empty or not.
   * You may override this if needed.
   */
  isEmpty() {
    return this.getCurrentValue() == null;
  }

  /**
   * Gets whether this value is required in an editing scenario.
   * @returns {boolean}
   */
  isRequired() {
    if (this._field == null) {
      return false;
    }
    return this._field.required;
  }

  /**
   * Indicates if this item is modified or not.
   * This will be true if the value has both a baseline and value set, OR if hasLocalChange is true.
   * @returns {boolean}
   */
  get isModified() {
    return this.get("state") === DataSourceState.CHANGE || this.get("hasLocalChange");
  }
  get isNormal() {
    const state = this.get("state");
    // Per DG-511 we include isDeletedLocal and isAddedLocal here because these can be driven by my _containerRecord
    return (state == null || state === DataSourceState.NORMAL) && !this.get("hasLocalChange") && !this.isDeletedLocal && !this.isAddedLocal && !this.isAdded && !this.isDeleted;
  }
  get isAdded() {
    if (this._containerRecord != null && this._containerRecord.isAdded) {
      // _containerRecord state takes precedence in this case
      return true;
    }
    return this.get("state") === DataSourceState.ADD;
  }
  get isDeleted() {
    if (this._containerRecord != null && this._containerRecord.isDeleted) {
      // _containerRecord state takes precedence in this case
      return true;
    }
    return this.get("state") === DataSourceState.DELETE;
  }
  get displayState() {
    // Note that localState takes precedence over state
    if (this.isAddedLocal) {
      return DataSourceState.ADD;
    }
    if (this.isDeletedLocal) {
      return DataSourceState.DELETE;
    }
    if (this.isAdded) {
      return DataSourceState.ADD;
    }
    if (this.isDeleted) {
      return DataSourceState.DELETE;
    }
    if (this.isModified) {
      return DataSourceState.CHANGE;
    }
    return DataSourceState.NORMAL;
  }
  get isDate() {
    return false;
  }
  get isDateTime() {
    return false;
  }
  get isNumber() {
    return false;
  }
  get isBoolean() {
    return false;
  }
  get isBinary() {
    return false;
  }
  get isReference() {
    return false;
  }
  get isString() {
    return false;
  }
  get isText() {
    return false;
  }
  get isVector() {
    return false;
  }
  get isRecord() {
    return false;
  }
  getTopLevelRecord() {
    if (this._containerRecord == null) {
      return null;
    }
    return this._containerRecord.getTopLevelRecord();
  }
  get isAddedLocal() {
    if (this._containerRecord != null && this._containerRecord.isAddedLocal) {
      // _containerRecord state takes precedence in this case
      return true;
    }
    return this.localState === DataSourceState.ADD;
  }
  get isDeletedLocal() {
    if (this._containerRecord != null && this._containerRecord.isDeletedLocal) {
      // _containerRecord state takes precedence in this case
      return true;
    }
    return this.localState === DataSourceState.DELETE;
  }

  /**
   * Determine is this object is marked for a RE_ADD which is defined as
   * having a DELETE state, but reverted locally.
   * @returns {boolean}
   */
  get isReAddedLocal() {
    return this.isDeleted && this.localState === DataSourceState.CHANGE;
  }

  /**
   * Called before issuing a saveData() command, this method prepares state
   * flags that the server expects to have set.
   */
  prepareValueForSave() {
    if (this.isAddedLocal) {
      // DG-267: Items marked as DELETE need to be marked as RE_ADD as a hint to server
      this.dataSourceSaveAction = this.isDeleted ? DataSourceSaveAction.RE_ADD : DataSourceSaveAction.ADD;
    } else if (this.isDeletedLocal) {
      this.dataSourceSaveAction = DataSourceSaveAction.DELETE;
    } else if (this.isModified && this.hasLocalChange) {
      // DG-367: need to check hasLocalChange too.
      // isModified will be true for things that came to client already modified from the server.
      // DG-267: Items marked as DELETE need to be marked as RE_ADD as a hint to server
      this.dataSourceSaveAction = this.isDeleted ? DataSourceSaveAction.RE_ADD : DataSourceSaveAction.CHANGE;
    } else {
      this.dataSourceSaveAction = null;
    }
  }

  /**
   * Called during deserialization of a DataSource from JSON, this method properly sets the state field and bubbles up any
   * relevant notifications.
   */
  initializeState() {
    if (this.state != null) {
      // state was set on server so honor it
      return;
    }
    if (this.getBaseline() != null) {
      // Assume the state is modified
      this.state = DataSourceState.CHANGE;
    } else {
      this.state = DataSourceState.NORMAL;
    }
  }

  /**
   * Marks this item as deleted (localState = DELETE).
   * @param undelete
   * @returns true if localState was changed
   */
  markForDelete(undelete = false) {
    let ret = false;
    if (undelete) {
      if (this.isDeletedLocal) {
        this.set("localState", DataSourceState.NORMAL);
        ret = true;
      } else if (this.isDeleted) {
        // This becomes the RE_ADD case
        this.set("localState", DataSourceState.CHANGE);
        ret = true;
      }
    } else {
      if (this.isDeleted) {
        // Like a revert since the item is already DELETEd
        this.set("localState", DataSourceState.NORMAL);
        ret = true;
      } else if (!this.isDeletedLocal) {
        this.set("localState", DataSourceState.DELETE);
        ret = true;
      }
    }
    return ret;
  }

  /**
   * Gets an attribute in the attributes
   * @param name
   */
  getAttribute(name) {
    if (this.attributeValues == null) {
      return null;
    }
    const value = this.attributeValues[name];
    return value == null ? null : value;
  }

  /**
   * Sets an attribute in the attributes property bag.
   * @param name
   * @param value
   */
  setAttribute(name, value) {
    if (this.attributeValues == null) {
      this.attributeValues = {};
    }
    this.attributeValues[name] = value;
  }

  /**
   * Accessor to get/set the subTypeID for this value, assuming the MHUB way of encoding
   * this which is as an attribute.
   * @returns {string | undefined}
   */
  get subTypeID() {
    return this.getAttribute("SubTypeID") || undefined;
  }
  set subTypeID(value) {
    if (value === undefined) {
      if (this.subTypeID) {
        delete this.attributeValues["SubTypeID"];
      }
    } else {
      this.setAttribute("SubTypeID", value);
    }
  }

  /**
   * Sets the currentValue based on some other value.
   * @param value Value to set.
   * Will coerce the value where possible, but may throw.
   */
  coerceAndSetCurrentValue(value) {
    const canonicalValue = this.coerceValue(value);
    if (this.areEqual(this.getCurrentValue(), canonicalValue)) {
      // No change so skip it
      return false;
    }
    this.set("currentValue", canonicalValue);
    return true;
  }
  initializeWithDefaultFieldValue(field) {
    // base implementation does nothing
  }

  /**
   * Compare function that can be used for sorting currentValues.
   * The default comparator is a locale-based string comparison.
   * Subclasses may override this function.
   * @param bvalue Value to compare against.
   * @return negative number if this value is less than bvalue,
   * positive number if bvalue is less than this value, 0 if equal.
   */
  compare(bvalue) {
    let a = this.getDisplayValue();
    let b = bvalue == null ? "" : bvalue.getDisplayValue();
    if (a == null && b == null) {
      return 0;
    }
    if (a == null) {
      // null goes at front
      return -1;
    } else if (b == null) {
      return 1;
    }
    return a.localeCompare(b);
  }
  static getValueTooltip(dsv, formatter) {
    if (dsv == null) {
      return undefined;
    }
    let tooltip;
    if (dsv.state === DataSourceState.CHANGE) {
      if (dsv.hasLocalChange) {
        // We have a CHANGE and a local change which means we have baseline, value and currentValue set
        tooltip = AbWebResources.dataSourceValueRenderer_changes2(dsv.getFormattedValue2(dsv.getValue(), formatter), dsv.getFormattedValue2(dsv.getBaseline(), formatter));
      } else {
        // We have a CHANGE but no local change
        tooltip = AbWebResources.dataSourceValueRenderer_changes1(dsv.getFormattedValue2(dsv.getBaseline(), formatter));
      }
    } else if (dsv.localState === DataSourceState.CHANGE) {
      tooltip = AbWebResources.dataSourceValueRenderer_changes1a(dsv.getFormattedValue2(dsv.getValue(), formatter));
    }
    if (tooltip) {
      tooltip = StringUtils.removeHtmlTags(tooltip);
      if (tooltip.length > 500) {
        // Truncate
        tooltip = tooltip.substr(0, 500) + "...";
      }
      return escapeQuotes(tooltip);
    }
    return tooltip;
    function escapeQuotes(input) {
      if (input.indexOf(`"`) >= 0) {
        return input.replaceAll(`"`, `&quot;`);
      } else {
        return input;
      }
    }
  }
}
_defineProperty(DataSourceValue, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=DataSourceValue.js.map
